import type { UseQueryOptions } from '@tanstack/react-query';
import { sdk, type P2pConfigRequest, type P2pTeamRowOut } from '@givmo/api-client';
import {
  campaignsGetCampaignOverviewQueryKey,
  p2pGetLeaderboardQueryKey,
} from '@givmo/api-client/query';
import { extractP2pOverview, type DecodedP2pOverview } from './p2p-decode';

/**
 * What an enable says where nobody chose teams. The server's default is on, so every enable says this
 * itself; the Campaign tab turns them on.
 */
export const TEAMS_OFF: P2pConfigRequest = { teams_enabled: false, supporter_teams_allowed: false };

export function campaignOptions(fundraiserId: string): UseQueryOptions<DecodedP2pOverview, Error> {
  const options = { path: { fundraiser_id: fundraiserId } };
  return {
    queryKey: campaignsGetCampaignOverviewQueryKey(options),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const { data } = await sdk.campaignsGetCampaignOverview({
        ...options,
        signal,
        throwOnError: true,
      });
      const overview = extractP2pOverview(data);
      if (!overview || overview.config.campaign_id !== fundraiserId) {
        throw new Error('The campaign response did not match this fundraiser.');
      }
      return overview;
    },
  };
}

/** Public preview uses the public grain, never management aggregates or locally assigned ranks. */
export function publicLeaderboardOptions(
  fundraiserId: string,
): UseQueryOptions<P2pTeamRowOut[], Error> {
  const options = { path: { campaign_id: fundraiserId } };
  return {
    queryKey: p2pGetLeaderboardQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.p2pGetLeaderboard({ ...options, signal, throwOnError: true });
      if (data?.campaign?.campaign_id !== fundraiserId || !Array.isArray(data.teams)) {
        throw new Error('The public leaderboard did not match this campaign.');
      }
      if (
        data.teams.some(
          (team) =>
            !team ||
            typeof team.team_id !== 'string' ||
            typeof team.name !== 'string' ||
            typeof team.raised_amount !== 'string' ||
            !/^\d+\.\d{2}$/.test(team.raised_amount) ||
            !Number.isSafeInteger(team.rank) ||
            team.rank < 1 ||
            !Number.isSafeInteger(team.donor_count) ||
            team.donor_count < 0 ||
            !Number.isSafeInteger(team.member_count) ||
            team.member_count < 0,
        )
      ) {
        throw new Error('The public leaderboard could not be read.');
      }
      return data.teams;
    },
  };
}
